/**
 * Tableau Error Interceptor
 * Monitors console errors for Tableau authentication failures and dispatches events
 * Uses a safer approach that doesn't intercept global functions
 */

class TableauErrorInterceptor {
  private isInitialized = false;
  private originalConsoleError: typeof console.error;

  constructor() {
    this.originalConsoleError = console.error;
  }

  initialize() {
    if (this.isInitialized) return;
    
    console.log('TableauErrorInterceptor: Initializing error monitoring');
    
    // Monitor console errors for Tableau 401 errors
    const self = this;
    console.error = function(...args) {
      // Call original console.error
      self.originalConsoleError.apply(console, args);
      
      // Check if this is a Tableau 401 error
      const message = args.join(' ');
      if (message.includes('401') && message.includes('tableau.com')) {
        console.log('TableauErrorInterceptor: Detected Tableau 401 error in console');
        
        // Dispatch custom event
        const event = new CustomEvent('tableau-auth-error', {
          detail: {
            message: message,
            timestamp: Date.now()
          }
        });
        
        window.dispatchEvent(event);
      }
    };

    // Also monitor for network errors in the console
    const originalError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      if (
        typeof message === "string" &&
        /^ResizeObserver loop/.test(message)
      ) {
        return true;
      }

      // Call original error handler
      if (originalError) {
        originalError.call(this, message, source, lineno, colno, error);
      }
      
      // Check if this is a Tableau-related error
      if (typeof message === 'string' && message.includes('tableau.com') && message.includes('401')) {
        console.log('TableauErrorInterceptor: Detected Tableau 401 error via window.onerror');
        
        // Dispatch custom event
        const event = new CustomEvent('tableau-auth-error', {
          detail: {
            message: message,
            source: source,
            timestamp: Date.now()
          }
        });
        
        window.dispatchEvent(event);
      }
    };

    this.isInitialized = true;
  }

  destroy() {
    if (!this.isInitialized) return;
    
    console.log('TableauErrorInterceptor: Destroying error monitoring');
    
    // Restore original functions
    console.error = this.originalConsoleError;
    
    this.isInitialized = false;
  }
}

// Create singleton instance
export const tableauErrorInterceptor = new TableauErrorInterceptor();

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  tableauErrorInterceptor.initialize();
}
